import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { getMemberTrackersApi } from "src/DAL/tracker/tracker";
import {
  clearLegacyJourneyDemoStorage,
  groupMemberTrackers,
  PRIMARY,
} from "./memberTrackersStorage";
import { JourneyLockChip } from "./JourneyPasswordGate";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const GuestJourneyCard = ({ tracker, meta, isPast, onOpen }) => {
  const chipColor = tracker.statusColor || PRIMARY;
  const chipLabel = isPast
    ? tracker.statusName || meta.statusLabel || "Left"
    : tracker.statusName || "Active";
  const actionLabel = !isPast && meta.canSubmit ? "Continue" : "View";

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "14px",
        bgcolor: "#fff",
        p: 2.25,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "11px",
              bgcolor:
                "color-mix(in srgb, var(--portal-theme-primary) 12%, white)",
              color: PRIMARY,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            {meta.isJourney ? (
              <RouteOutlinedIcon fontSize="small" />
            ) : (
              <FlagOutlinedIcon fontSize="small" />
            )}
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 16.5,
              color: INK,
              letterSpacing: "-0.02em",
              lineHeight: 1.3,
            }}
          >
            {tracker.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: 0.75,
          }}
        >
          {meta.isPasswordLocked ? <JourneyLockChip /> : null}
          <Chip
            size="small"
            label={chipLabel}
            sx={{
              height: 24,
              fontWeight: 700,
              fontSize: 11.5,
              bgcolor: `${chipColor}18`,
              color: chipColor,
            }}
          />
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
          <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>
            Progress
            {meta.currentStep ? ` · Next: ${meta.currentStep.name}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 12, color: INK, fontWeight: 700 }}>
            {meta.progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={meta.progress}
          sx={{
            height: 7,
            borderRadius: 99,
            bgcolor: "#eef2f6",
            "& .MuiLinearProgress-bar": { borderRadius: 99, bgcolor: PRIMARY },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant={actionLabel === "Continue" ? "contained" : "outlined"}
          onClick={onOpen}
        >
          {actionLabel}
        </Button>
      </Box>
    </Box>
  );
};

const JourneyGroup = ({ title, rows, isPast, onOpen }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      sx={{ fontWeight: 800, fontSize: 18, color: INK, letterSpacing: "-0.02em", mb: 1.75 }}
    >
      {title}
      <Box component="span" sx={{ ml: 1, fontWeight: 650, fontSize: 14, color: MUTED }}>
        ({rows.length})
      </Box>
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr" },
        gap: 2,
      }}
    >
      {rows.map(({ tracker, meta }) => (
        <GuestJourneyCard
          key={tracker.id}
          tracker={tracker}
          meta={meta}
          isPast={isPast}
          onOpen={() => onOpen(tracker.id)}
        />
      ))}
    </Box>
  </Box>
);

/** Lightweight dashboard for journey guests (magic-link login). */
export default function GuestDashboard() {
  const navigate = useNavigate();
  const { userInfo } = useContentSetting();
  const [trackers, setTrackers] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error
  const [loadMessage, setLoadMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadState("loading");
      try {
        const res = await getMemberTrackersApi();
        if (cancelled) return;
        if (res?.code === 200 && Array.isArray(res.trackers)) {
          setTrackers(res.trackers);
          setLoadState("ready");
          return;
        }
        setTrackers([]);
        setLoadState("error");
        setLoadMessage(res?.message || "Could not load your journeys");
      } catch (_err) {
        if (cancelled) return;
        setTrackers([]);
        setLoadState("error");
        setLoadMessage("Could not load your journeys");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userInfo?._id]);

  useEffect(() => {
    clearLegacyJourneyDemoStorage();
  }, []);

  const grouped = useMemo(() => groupMemberTrackers(trackers), [trackers]);

  const firstName = String(userInfo?.first_name || "").trim();
  const openJourney = (id) => navigate(`/trackers/${id}`);
  const isEmpty = grouped.my.length === 0 && grouped.left.length === 0;

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-lg-8 col-sm-12">
          <h2 style={{ marginBottom: 6 }}>
            {firstName ? `Welcome, ${firstName}` : "Welcome"}
          </h2>
          <Typography sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.5 }}>
            Here's how your journey is going.
          </Typography>
        </div>
      </div>

      {loadState === "loading" ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : loadState === "error" ? (
        <Typography sx={{ color: MUTED, fontSize: 14.5 }}>{loadMessage}</Typography>
      ) : isEmpty ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            border: `1px dashed ${BORDER}`,
            borderRadius: "14px",
            bgcolor: "#f8fafc",
          }}
        >
          <Typography sx={{ color: MUTED, fontSize: 14 }}>
            You're not on any journeys yet.
          </Typography>
        </Box>
      ) : (
        <>
          {grouped.my.length > 0 ? (
            <JourneyGroup title="My journeys" rows={grouped.my} onOpen={openJourney} />
          ) : null}
          {grouped.left.length > 0 ? (
            <JourneyGroup
              title="Past journeys"
              rows={grouped.left}
              isPast
              onOpen={openJourney}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
